import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { downloadDocument } from "../lib/fetchDocument";
import { DocumentPreview } from "../components/DocumentPreview";
import { PreviewErrorBoundary } from "../components/PreviewErrorBoundary";
import { BrandLoader } from "../components/BrandLoader";
import { useDelayedLoader } from "../hooks/useDelayedLoader";
import { IconClose, IconDownload } from "../components/Icons";
import { DEFAULT_VIEWER_LOGO } from "../components/ViewerPortalHeader";

type Doc = {
  id: string;
  filename: string;
  mime_type?: string;
  display_name?: string;
  status?: string;
};

function docLabel(d: Doc) {
  return d.display_name?.trim() || d.filename;
}

export function AdminPreviewPage() {
  const { roomId = "", documentId = "" } = useParams();
  const [roomName, setRoomName] = useState("Room");
  const [doc, setDoc] = useState<Doc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewPage, setPreviewPage] = useState({ current: 1, total: 0 });
  const showLoader = useDelayedLoader(loading);

  useEffect(() => {
    if (!roomId || !documentId) {
      setError("Missing room or document.");
      setLoading(false);
      return;
    }

    Promise.all([
      api<{ room: { name: string } }>(`/api/v1/rooms/${roomId}`, { admin: true }),
      api<{ documents: Doc[] }>(`/api/v1/rooms/${roomId}/files`, { admin: true }),
    ])
      .then(([roomRes, filesRes]) => {
        setRoomName(roomRes.room?.name || "Room");
        const hit = filesRes.documents.find((d) => d.id === documentId);
        if (!hit) {
          setError("This file is no longer available.");
          return;
        }
        if (hit.status && hit.status !== "ready") {
          setError("This file is still processing. Try again in a moment.");
          return;
        }
        setDoc(hit);
      })
      .catch((e) => setError(String((e as Error).message || e)))
      .finally(() => setLoading(false));
  }, [roomId, documentId]);

  const downloadFile = () => {
    if (!doc) return;
    downloadDocument(`/api/v1/files/${doc.id}/download`, doc.filename, { admin: true }).catch((e) =>
      setError(String((e as Error).message || e)),
    );
  };

  if (loading) {
    return (
      <div className="preview-standalone-page preview-standalone-page--loading">
        {showLoader ? <BrandLoader size="lg" label="Opening preview…" /> : null}
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="preview-standalone-page preview-standalone-page--loading">
        <div className="card preview-standalone__error-card" style={{ maxWidth: 420, padding: 20, textAlign: "center" }}>
          <p role="alert">{error || "Could not open this file."}</p>
          <div className="row toolbar" style={{ justifyContent: "center", marginTop: 12 }}>
            <Link to={`/rooms/${roomId}`} className="secondary" style={{ textDecoration: "none" }}>
              Return to room
            </Link>
            <button type="button" className="secondary" onClick={() => window.close()}>
              Close tab
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="preview-standalone-page preview-standalone-page--admin">
      <header className="preview-standalone__bar">
        <div className="preview-standalone__brand">
          <img className="preview-standalone__logo" src={DEFAULT_VIEWER_LOGO} alt="" />
          <div className="preview-standalone__meta">
            <strong className="preview-standalone__title">{docLabel(doc)}</strong>
            <span className="preview-standalone__room">{roomName}</span>
          </div>
        </div>
        <div className="preview-standalone__status">
          {previewPage.total > 0 ? (
            <span className="preview-standalone__page">
              {previewPage.current} / {previewPage.total}
            </span>
          ) : null}
          <span className="preview-standalone__tag preview-standalone__tag--admin">Admin preview</span>
        </div>
        <div className="preview-standalone__actions">
          <button type="button" className="preview-standalone__action" onClick={downloadFile} aria-label="Download">
            <IconDownload aria-hidden />
          </button>
          <button
            type="button"
            className="preview-standalone__action"
            onClick={() => window.close()}
            aria-label="Close preview"
          >
            <IconClose aria-hidden />
          </button>
        </div>
      </header>

      <main className="preview-standalone__main">
        <PreviewErrorBoundary>
          <DocumentPreview
            key={documentId}
            layout="standalone"
            url={`/api/v1/files/${documentId}/content`}
            filename={doc.filename}
            mimeType={doc.mime_type}
            documentId={documentId}
            admin
            onCurrentPageChange={(current, total) => setPreviewPage({ current, total })}
          />
        </PreviewErrorBoundary>
      </main>

      <footer className="preview-standalone__footer">
        <Link to={`/rooms/${roomId}`}>Back to room</Link>
        <span>Admin preview, no visitor watermark</span>
      </footer>
    </div>
  );
}
